import { dateToTimestamp } from "api/firebase";
import { round } from "lodash";
import { v4 as uuid } from "uuid";
import { Invoice, InvoiceStatus, OrderInvoices } from "ts/api/order";
import { PaymentMethodType } from "ts/api/paymentMethod";
import { FullProduct } from "ts/api/product";
import {
  calculateFullProductPrice,
  calculateSupplierProductsFeesAmount,
} from "tools/product/productCalculations";
import {
  calculateSupplierPaymentFeeAmount,
  calculateSupplierPlatformFeeAmount,
  getDiscountAmount,
} from "view/apps/Exhibitor/pages/Cart/helpers";
import getTaxAmount from "tools/common/getTaxAmount";
import { mapOrderProduct } from "view/apps/Exhibitor/pages/Cart/helpers/mapOrderProduct";

import { IOrganiser, IShow } from "@evennt-tech/api-v2-core";
import { BankTransferBillingDetails } from "store/exhibitors/cartStepper/slice";
import getTransfers from "../getTransfers";

type CreateInvoicesParams = {
  fullProducts: FullProduct[][];
  organiser: IOrganiser;
  show: IShow;
  poNumbers: Record<string, string>;
  transferredPaymentDescription: string;
  orderId: string;
  billingMetadata: BankTransferBillingDetails;
};

const getInvoices = async ({
  fullProducts,
  organiser,
  show,
  poNumbers,
  transferredPaymentDescription,
  orderId,
  billingMetadata,
}: CreateInvoicesParams): Promise<OrderInvoices> => {
  const mapProducts = async (products: FullProduct[]) => {
    const invoiceId = uuid();
    const feesAmount = calculateSupplierProductsFeesAmount({
      products,
      includeFees: true,
    });
    const productsWithoutDiscountAmount = round(
      products.reduce(
        (sum, product) =>
          sum +
          calculateFullProductPrice({
            product,
            includeFees: true,
          }),
        0
      ),
      2
    );
    const discountAmount = getDiscountAmount(products, true);
    const paymentFeeAmount = organiser?.organiserCompanyToOrganisers?.[0]
      ?.organiserCompany?.paymentFee
      ? round(calculateSupplierPaymentFeeAmount(products), 2)
      : 0;
    const platformFeeAmount = organiser?.organiserCompanyToOrganisers?.[0]
      ?.organiserCompany?.fee
      ? calculateSupplierPlatformFeeAmount(products)
      : null;
    const productsAmount = Math.max(
      0,
      productsWithoutDiscountAmount - discountAmount
    );
    const subtotalAmount = productsAmount + feesAmount + paymentFeeAmount;

    const taxAmount = getTaxAmount(subtotalAmount, show.salesTaxPercentage);
    const totalAmount = round(subtotalAmount + taxAmount, 2);
    const mappedProducts = products.map((product) =>
      mapOrderProduct(product, PaymentMethodType.bankTransfer)
    );
    const firstProduct = products[0];
    const productOwnerId = products[0].owner.id;
    const poNumber = Object.values(poNumbers || {}).length
      ? poNumbers[`${productOwnerId}_${firstProduct.id}`]
      : "";
    const transfers = await getTransfers({
      products,
      organiser,
      show,
      transferredPaymentDescription,
      orderId,
      invoiceNumber: invoiceId,
      billingMetadata,
    });
    const invoice: Invoice = {
      id: invoiceId,
      status: "draft" as InvoiceStatus,
      subtotalAmount,
      taxAmount,
      totalAmount,
      paymentFeeAmount,
      ...(platformFeeAmount && { platformFeeAmount }),
      ...(discountAmount && { discountAmount }),
      poNumber,
      products: mappedProducts,
      createdAt: dateToTimestamp(new Date()),
      transfers,
    };
    return invoice;
  };

  const invoices = await Promise.all(
    fullProducts.map(async (products) => {
      return mapProducts(products);
    })
  );

  return invoices.reduce<OrderInvoices>(
    (acc, cur) => ({
      ...acc,
      [cur.id]: cur,
    }),
    {}
  );
};
export default getInvoices;

export const getInvoicesOrganiserRevenue = (invoices: OrderInvoices) =>
  round(
    Object.values(invoices).reduce((acc: number, cur: Invoice) => {
      const organiserTransfer = cur.transfers.find(
        (transfer) => transfer.metadata.role === "organiser"
      );

      return organiserTransfer ? organiserTransfer.amount + acc : acc;
    }, 0),
    2
  );
